import { createApi } from '@reduxjs/toolkit/query/react';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice.ts';
import BaseQuery from './base.api.ts';
import { handleFilterParams } from './filterParamUtils';
import { validateBlobResponse } from '../utils/helpers';
import {
	type Asset,
	NonLeasedAssetBulkEndpointByUpdateType,
	type UserDefinedFieldHeadings,
	type UserDefinedFieldValues,
} from './models/asset.model';
import type { AssetPageSummary } from './models/assetPageSummary.model.ts';
import type { Audit } from './models/audit.model.ts';
import type { FilterSearchRequestParams } from './models/filters.model.ts';
import type { AssetsByManufacturerGraph, Graph } from './models/graph.model.ts';
import type { InvoiceAsset } from './models/invoiceAsset.model.ts';
import type { NonLeasedAsset } from './models/nonLeasedAsset.model.ts';
import type { PaginatedResults } from './models/paginatedResults.model.ts';
import { setAuditError, setAuditLoading, setAuditLogs } from '../redux/slices/assetSlice.ts';

export interface AssetFilters {
	userId: string;
	search?: string;
	status?: string;
	[key: string]: string | undefined;
}
export interface AssetAggregateSummary {
	totalEquipmentCost: number;
	totalContracts: number;
	totalAssets: number;
}
export interface Address {
	accountId: number | undefined;
	sourceId: number | undefined;
	installAddress1: string | undefined;
	installAddress2: string | undefined;
	installAddress3: string | undefined;
	installAddress4: string | undefined;
	installCity: string | undefined;
	installCounty: string | undefined;
	installProvince: string | undefined;
	installState: string | undefined;
	installPostalCode: string | undefined;
	installCountry: string | undefined;
}
export interface CsvUploadResultDto {
	success: boolean;
	errors: CsvUploadResultDtoError[];
}
export interface CsvUploadResultDtoError {
	row: number;
	column: string;
	message: string;
}

export const assetsApi = createApi({
	reducerPath: 'assetsApi',
	baseQuery: BaseQuery('/v1/assets'),
	tagTypes: ['Asset', 'Audit', 'Summary', 'Graph', 'Export', 'NonLeasedAssets', 'LeasedUdfStatus'],
	endpoints: (builder) => ({
		getAssetAddresses: builder.query<
			Address[],
			{
				accountId: number;
				accountTypeCode: string;
				sourceId: number;
			}
		>({
			query: ({ accountId, accountTypeCode, sourceId }) => {
				return `addresses?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}`;
			},
		}),

		// Get all assets (optionally filtered)
		getAssets: builder.query<
			PaginatedResults<Asset>,
			{
				accountId: number;
				accountTypeCode: string;
				sourceId: number;
				filters?: Record<string, any>;
				currentPage: number;
				pageSize: number;
				sortDirection?: string;
				sortField?: string;
			}
		>({
			query: ({
				accountId,
				accountTypeCode,
				sourceId,
				filters,
				currentPage,
				pageSize,
				sortDirection = '',
				sortField = '',
			}) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				return `?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection}&sortField=${sortField}&${params.toString()}`;
			},
			providesTags: ['Asset'],
		}),

		// Get all assets (optionally filtered)
		getAssetsByContract: builder.query<
			PaginatedResults<Asset>,
			{
				contractNumber: string;
				accountId: number;
				accountTypeCode: string;
				sourceId: number;
				filters?: Record<string, any>;
				currentPage: number;
				pageSize: number;
				sortDirection?: string;
				sortField?: string;
			}
		>({
			query: ({
				contractNumber,
				accountId,
				accountTypeCode,
				sourceId,
				filters,
				currentPage,
				pageSize,
				sortDirection = '',
				sortField = '',
			}) => {
				const params = new URLSearchParams();
				if (filters) {
					Object.entries(filters).forEach(
						([k, v]) => k != 'contractNumber' && v && params.append(k, v)
					);
					handleFilterParams(filters, params);
				}

				if (params.size > 0) {
					return `?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&contractNumber=${contractNumber}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection}&sortField=${sortField}&${params.toString()}`;
				}

				return `?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&contractNumber=${contractNumber}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection}&sortField=${sortField}`;
			},
			providesTags: ['Asset'],
		}),

		getNonLeasedAssets: builder.query<
			PaginatedResults<NonLeasedAsset>,
			{
				accountId: number;
				accountTypeCode: string;
				sourceId: number;
				filters?: Record<string, any>;
				currentPage: number;
				pageSize: number;
				sortDirection?: string;
				sortField?: string;
			}
		>({
			query: ({
				accountId,
				accountTypeCode,
				sourceId,
				filters,
				currentPage,
				pageSize,
				sortDirection = '',
				sortField = '',
			}) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				return `non-leased-assets?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection}&sortField=${sortField}&${params.toString()}`;
			},
			providesTags: ['NonLeasedAssets'],
		}),

		// Get single asset
		getAsset: builder.query<Asset, string>({
			query: (assetNumber) => `v1/assets/${assetNumber}`,
			providesTags: (_result, _error, assetNumber) => [{ type: 'Asset', assetNumber }],
		}),

		// Get Asset Page Summary
		getAssetPageSummary: builder.query<AssetPageSummary, FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `asset-page-summary?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&${paramString}`;
			},
			providesTags: ['Asset'],
		}),

		// Create new asset
		createAsset: builder.mutation<Asset, Partial<Asset>>({
			query: (newAsset) => ({
				url: '',
				method: 'POST',
				body: newAsset,
			}),
			invalidatesTags: ['Asset'],
		}),

		// Get audit logs (paginated, sortable)
		getAuditLogs: builder.query<
			PaginatedResults<Audit>,
			{
				assetId: string;
				page?: number;
				pageSize?: number;
				sortDirection?: string;
				sortField?: string;
			}
		>({
			query: ({ assetId, page = 0, pageSize = 10, sortDirection = '', sortField = '' }) => {
				return `${assetId}/audit-logs?page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortDirection=${sortDirection}`;
			},
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				dispatch(setAuditLoading(true));

				try {
					const { data } = await queryFulfilled;
					dispatch(setAuditLogs(data?.content ?? []));
				} catch (error) {
					dispatch(setAuditError('Failed to load audit logs'));
				}
			},

			providesTags: ['Audit'],
		}),

		getAssetAggregateSummary: builder.query<AssetAggregateSummary, FilterSearchRequestParams>({
			query: ({ sourceId, accountId, accountTypeCode, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `aggregate-summary?sourceId=${sourceId}&accountId=${accountId}&accountTypeCode=${accountTypeCode}${paramString}`;
			},
			providesTags: ['Summary'],
		}),

		getAssetCountByCategory: builder.query<Graph[], FilterSearchRequestParams>({
			query: ({ sourceId, accountId, accountTypeCode, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `asset-by-category-calculations?sourceId=${sourceId}&accountId=${accountId}&accountTypeCode=${accountTypeCode}${paramString}`;
			},
			providesTags: ['Graph'],
		}),

		getAssetCountByManufacturer: builder.query<
			AssetsByManufacturerGraph,
			FilterSearchRequestParams
		>({
			query: ({ sourceId, accountId, accountTypeCode, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `asset-by-manufacturer-calculations?sourceId=${sourceId}&accountId=${accountId}&accountTypeCode=${accountTypeCode}${paramString}`;
			},
			providesTags: ['Graph'],
		}),

		getAssetsByClientAttributes: builder.query<Graph[], FilterSearchRequestParams>({
			query: ({ sourceId, accountId, accountTypeCode, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				// TODO: When auth in place, remove userId and get customer id on backend
				return `asset-by-client-attributes?sourceId=${sourceId}&accountId=${accountId}&accountTypeCode=${accountTypeCode}${paramString}`;
			},
			providesTags: ['Graph'],
		}),
		exportAssets: builder.query<Blob, FilterSearchRequestParams>({
			async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { accountId, accountTypeCode, sourceId, filters, selectedRows = []  } = arg;
				const params = new URLSearchParams();

				// A selection takes precedence over the active filters.
				if (selectedRows.length > 0) {
					const ids = selectedRows
						.map((row) => row.id)
						.filter((id) => id !== undefined && id !== null);
					params.append('assetIds', ids.join(','));
				} else if (filters) {
					handleFilterParams(filters, params);
				}
				const paramString = params.size > 0 ? `&${params.toString()}` : '';

				const response = await fetchWithBQ({
					url: `export?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`,
					method: 'GET',
					responseHandler: (r: Response) => r.blob(),
				});

				return validateBlobResponse(response);
			},
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			keepUnusedDataFor: 5,
			providesTags: ['Export'],
		}),

		exportNonLeasedAssets: builder.query<Blob, FilterSearchRequestParams>({
			async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { accountId, accountTypeCode, sourceId, filters, selectedRows = [] } = arg;
				const params = new URLSearchParams();

				// A selection takes precedence over the active filters.
				if (selectedRows.length > 0) {
					const ids = selectedRows
						.map((row) => row.id)
						.filter((id) => id !== undefined && id !== null);
					params.append('nonAssetIds', ids.join(','));
				} else if (filters) {
					handleFilterParams(filters, params);
				}
				const paramString = params.size > 0 ? `&${params.toString()}` : '';

				const response = await fetchWithBQ({
					url: `non-leased-assets/export?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`,
					method: 'GET',
					responseHandler: (r: Response) => r.blob(),
				});

				return validateBlobResponse(response);
			},
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			keepUnusedDataFor: 5,
			providesTags: ['Export'],
		}),

		getAssetCountByEndDate: builder.query<Graph[], FilterSearchRequestParams>({
			query: ({ sourceId, accountId, accountTypeCode, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `/asset-count-by-asset-end-date?sourceId=${sourceId}&accountId=${accountId}&accountTypeCode=${accountTypeCode}${paramString}`;
			},
			providesTags: ['Graph'],
		}),

		getAssetUserDefinedFields: builder.query<UserDefinedFieldHeadings, { accountId: number }>({
			query: ({ accountId }) => {
				return `/user-defined-headings?accountId=${accountId}`;
			},
			providesTags: ['Asset'],
		}),

		uploadNonLeasedAssets: builder.mutation<
			CsvUploadResultDto,
			{
				file: File;
				sourceId: number;
				uploadType: string;
			}
		>({
			query: ({ file, sourceId, uploadType }) => {
				const endpoint: string =
					NonLeasedAssetBulkEndpointByUpdateType[
						uploadType as keyof typeof NonLeasedAssetBulkEndpointByUpdateType
					];
				const formData = new FormData();
				formData.append('uploadFile', file);
				formData.append('sourceId', String(sourceId));
				return {
					url: endpoint,
					method: 'POST',
					body: formData,
				};
			},
			extraOptions: { includeJson: false },
			invalidatesTags: ['NonLeasedAssets'],
		}),
		uploadLeasedAssetUDFChanges: builder.mutation<
			CsvUploadResultDto,
			{
				file: File;
				accountId: number;
			}
		>({
			query: ({ file, accountId }) => {
				const formData = new FormData();
				formData.append('uploadFile', file);
				formData.append('accountId', String(accountId));
				return {
					url: 'upload',
					method: 'POST',
					body: formData,
				};
			},
			extraOptions: { includeJson: false },
			invalidatesTags: ['LeasedUdfStatus'],
		}),

		updateNonLeasedUdfValues: builder.mutation<
			any,
			{
				assetIds: Asset[];
				udfValues: UserDefinedFieldValues;
			}
		>({
			query: ({ assetIds, udfValues }) => {
				const body: Asset[] = assetIds.map((assetId) => ({
					id: assetId.id,
					...udfValues,
				}));

				return {
					url: 'non-leased-assets/udf-values',
					method: 'PUT',
					body: JSON.stringify(body),
					headers: { 'Content-Type': 'application/json' },
				};
			},
			invalidatesTags: ['NonLeasedAssets'],
		}),

		updateLeasedUdfValues: builder.mutation<
			any,
			{
				account: number;
				assets: Asset[];
				udfValues: UserDefinedFieldValues;
			}
		>({
			query: ({ assets, udfValues, account }) => {
				const body: Asset[] = assets.map((assetId) => ({
					instItemAssetId: assetId.instItemAssetId,
					...udfValues,
				}));
				return {
					url: 'udf-values?account=' + account,
					method: 'PUT',
					body: JSON.stringify(body),
					headers: { 'Content-Type': 'application/json' },
				};
			},
			invalidatesTags: ['LeasedUdfStatus'],
		}),
		getUpdateLeasedUdfValuesStatus: builder.query<{ jobStatus: string }, any>({
			query: () => {
				return {
					url: 'udf-values/upload-status',
					method: 'GET',
				};
			},
			providesTags: ['LeasedUdfStatus'],
		}),
		acknowledgeUpdatedUdfValues: builder.mutation<any, void>({
			query: () => {
				return {
					url: '/udf-values/upload-status/ack',
					method: 'PUT',
				};
			},
			invalidatesTags: ['LeasedUdfStatus'],
		}),
		deleteNonLeasedAssets: builder.mutation<void, { ids: number[] }>({
			query: ({ ids }) => {
				return {
					url: 'non-leased-assets/delete',
					method: 'POST',
					body: JSON.stringify(ids),
					headers: { 'Content-Type': 'application/json' },
				};
			},
			invalidatesTags: ['NonLeasedAssets'],
		}),
		getAssetsByInvoiceNumber: builder.query<
			PaginatedResults<InvoiceAsset>,
			{
				invoiceNumber: string;
				page?: number;
				pageSize?: number;
				sortDirection?: string;
				sortField?: string;
				filters?: Record<string, any>;
			}
		>({
			query: ({
				invoiceNumber,
				page = 0,
				pageSize = 50,
				sortDirection = '',
				sortField = '',
				filters,
			}) => {
				const params = new URLSearchParams();
				params.append('invoiceNumber', invoiceNumber);
				params.append('page', String(page));
				params.append('pageSize', String(pageSize));
				if (sortDirection) params.append('sortDirection', sortDirection);
				if (sortField) params.append('sortField', sortField);
				if (filters) handleFilterParams(filters, params);
				return `invoice-data?${params.toString()}`;
			},
		}),
	}),
});

export const {
	useGetAssetPageSummaryQuery,
	useGetAssetsQuery,
	useGetAssetsByContractQuery,
	useGetNonLeasedAssetsQuery,
	useGetAssetQuery,
	useCreateAssetMutation,
	useGetAuditLogsQuery,
	useLazyGetAuditLogsQuery,
	useGetAssetAddressesQuery,
	useGetAssetAggregateSummaryQuery,
	useGetAssetCountByCategoryQuery,
	useGetAssetCountByManufacturerQuery,
	useGetAssetsByClientAttributesQuery,
	useLazyExportAssetsQuery,
	useLazyExportNonLeasedAssetsQuery,
	useGetAssetCountByEndDateQuery,
	useLazyGetAssetsQuery,
	useLazyGetAssetsByContractQuery,
	useLazyGetNonLeasedAssetsQuery,
	useGetAssetUserDefinedFieldsQuery,
	useUploadNonLeasedAssetsMutation,
	useUpdateNonLeasedUdfValuesMutation,
	useUpdateLeasedUdfValuesMutation,
	useGetUpdateLeasedUdfValuesStatusQuery,
	useAcknowledgeUpdatedUdfValuesMutation,
	useUploadLeasedAssetUDFChangesMutation,
	useDeleteNonLeasedAssetsMutation,
	useGetAssetsByInvoiceNumberQuery,
	useLazyGetAssetsByInvoiceNumberQuery,
} = assetsApi;
